import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/PageHeader";
import { RegateImport } from "@/components/RegateImport";
import { publicFile } from "@/components/landing/LandingData";
import { api, errMsg, fmtDate, todayStr, BOAT_CATEGORIES } from "@/lib/api";

const EMPTY = { categoria: "", titolo: "", data: todayStr() };

const UploadForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.categoria || !file) return toast.error("Seleziona classe e file");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/admin/ranking", fd);
      toast.success("Ranking pubblicata");
      setF(EMPTY);
      setFile(null);
      e.target.reset();
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="ranking-form">
      <div>
        <Label>Classe</Label>
        <Select value={f.categoria} onValueChange={(v) => setF({ ...f, categoria: v })}>
          <SelectTrigger data-testid="ranking-categoria-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{BOAT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Data</Label><Input type="date" required value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="ranking-data-input" /></div>
      <div className="sm:col-span-2"><Label>Titolo</Label><Input required maxLength={150} value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} placeholder="Es. Ranking list nazionale 2026" data-testid="ranking-titolo-input" /></div>
      <div className="sm:col-span-2"><Label>File PDF o Excel (max 10 MB)</Label><Input type="file" accept=".pdf,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ranking-file-input" /></div>
      <Button type="submit" disabled={busy} className="w-fit bg-emerald-600 hover:bg-emerald-700" data-testid="ranking-submit-button">{busy ? "Caricamento…" : "Pubblica ranking"}</Button>
    </form>
  );
};

export const RankingAdmin = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/public/ranking").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa ranking list?")) return;
    try {
      await api.delete(`/admin/ranking/${id}`);
      toast.success("Ranking eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Panel>
        <h2 className="font-heading text-2xl uppercase mb-4">Carica ranking list</h2>
        <UploadForm onDone={load} />
        <div className="border-t mt-6 pt-5">
          <h3 className="font-heading text-lg uppercase mb-2">Calendario regate da Excel</h3>
          <RegateImport onDone={() => {}} />
        </div>
      </Panel>
      <Panel>
        <h2 className="font-heading text-2xl uppercase mb-4">Ranking pubblicate</h2>
        {items.length === 0 && <p className="text-muted-foreground" data-testid="ranking-admin-empty">Nessuna ranking list caricata.</p>}
        <ul className="divide-y">
          {items.map((r) => (
            <li key={r.id} className="py-3 flex items-center justify-between gap-3" data-testid={`ranking-admin-row-${r.id}`}>
              <a href={publicFile(r.file_id)} target="_blank" rel="noreferrer" className="hover:underline">
                <span className="font-medium">{r.categoria} · {r.titolo}</span>
                <span className="block text-xs text-muted-foreground">{fmtDate(r.data)} · {r.filename}</span>
              </a>
              <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`ranking-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};
